import { useEffect, useRef, useCallback } from 'react'
import * as d3 from 'd3'

const getActiveValue = (item, selectedYear) => {
    if (!item) return 0;
    
    // Cas 1 : Nouvelle structure (la règle elle-même)
    if (item.application_periods) {
        const activePeriod = item.application_periods.find(p => 
            selectedYear >= p.start_year && (!p.end_year || selectedYear <= p.end_year)
        );
        return activePeriod && activePeriod.value !== null ? parseFloat(activePeriod.value) : 0;
    }
    
    // Cas 2 : Ancienne structure (pour l'objet "period" de la fenêtre d'exclusivité)
    if (item.value_info) {
        if (item.value_info.value_yearly !== null && item.value_info.value_yearly !== undefined) {
            return parseFloat(item.value_info.value_yearly);
        }
        if (item.value_info.value_years && item.value_info.value_years.length > 0) {
            return parseFloat(item.value_info.value_years[0].value);
        }
    }
    
    return 0;
};

export function TimelineComponent({ data, onRuleClick, selectedRuleId, selectedYear }) {

  const chartRef = useRef(null);

  const onRuleClickRef = useRef(onRuleClick);
  useEffect(() => {
      onRuleClickRef.current = onRuleClick;
  }, [onRuleClick]);

  const drawTimeline = useCallback((rawData) => {
    d3.select(chartRef.current).selectAll("*").remove();
    const container = d3.select(chartRef.current);

    const margin = { top: 0, right: 20, bottom: 0, left: 20 };
    const width = 300 - margin.left - margin.right;
    const height = 80 - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const exploitationPoints = rawData;
    let flatPeriods = []
    exploitationPoints.forEach(parent => {
        // 3. UTILISATION DU NOUVEAU HELPER AVEC L'ANNÉE POUR LE POINT DE DÉPART
        const parentStartMonth = getActiveValue(parent, selectedYear) || 0;

        if (!parent.period || !parent.period.rule_type) return;

        flatPeriods.push({
            ruleType: parent.period.rule_type,
            startTimeMonth: parentStartMonth,
            // 4. UTILISATION DU HELPER POUR LA DURÉE DE LA FENÊTRE
            durationValue: getActiveValue(parent.period, selectedYear), 
            unit: parent.period.value_unit || parent.period.value_info?.value_unit || 'mois',
            originalRule: parent 
        });
    });

    const xScale = d3.scaleLinear()
      .domain([0, 36]) 
      .range([0, width]); 

    const xScaleDays = d3.scaleLinear()
      .domain([0, 1095]) 
      .range([0, width]); 

    const xAxis = d3.axisBottom(xScale)
      .tickValues([0,6,12,18,24,30,36])
      .tickFormat(d => `${d} mois`);

    svg.append("g")
      .attr("class", "x-axis")
      .style("font-size","0.6rem")
      .attr("transform", `translate(0, ${height / 1.5})`)
      .call(xAxis);

    const lineX = xScale(0);
    const lineY1 = height / 1.5;
    const lineY2 = height / 2 - 25;

    svg.append("line")
      .attr("x1", lineX).attr("y1", lineY1)
      .attr("x2", lineX).attr("y2", lineY2)
      .attr("stroke", "#555").attr("stroke-width", 1);

    const labelGroup = svg.append("g").attr("class", "start-label-group");
    const rectPaddingY = 2;
    const rectWidth = 57;
    const rectHeight = 14;

    labelGroup.append("rect")
      .attr("x", xScale(0) - 2 - 10)
      .attr("y", lineY2 - rectPaddingY - 10)
      .attr("width", rectWidth).attr("height", rectHeight)
      .attr("fill", "#e0e0e0").attr("rx", 1).attr("ry", 1);

    labelGroup.append("text")
      .attr("class", "start-label")
      .attr("x", xScale(0) - 10)
      .attr("y", lineY2 - rectPaddingY)
      .attr("text-anchor", "start")
      .style("font-size", "0.6rem").style("font-weight", "500").style("fill", "#000")
      .text("Sortie en salles");

    // --- Points ---
    svg.selectAll(".exploitation-point")
      .data(exploitationPoints)
      .enter()
      .append("circle")
      .attr("class", "exploitation-point")
      .attr("cx", (d) => xScale(getActiveValue(d, selectedYear)))
      .attr("cy", height / 1.5)
      .attr("r", 5)
      // HIGHLIGHT LOGIC: Check ID against selectedRuleId
      .attr("fill", (d) => (d.id === selectedRuleId) ? "#fde68a" : "#8e82ca")
      .style("cursor", onRuleClick ? "pointer" : "default")
      .on("click", (event, d) => {
          if (onRuleClickRef.current) {
              onRuleClickRef.current(d); 
              event.stopPropagation();
          }
      });

    svg.selectAll(".exploitation-label")
      .data(exploitationPoints)
      .enter()
      .append("text")
      .attr("class", "exploitation-label")
      .attr("x", (d) => xScale(getActiveValue(d, selectedYear)))
      .attr("y", height / 1.5 - 10)
      .attr("text-anchor", "middle")
      .style("font-size","0.6rem").style("fill", "#333")
      .text((d) => `${getActiveValue(d, selectedYear)} mois`);

    // --- Periods ---
    svg.selectAll(".exclusivity-period")
      .data(flatPeriods)
      .enter()
      .append("rect")
      .attr("class", "exclusivity-period")
      .attr("x", (d) => xScale(d.startTimeMonth))
      .attr("y", height / 1.5 - 20)
      .attr("width", (d) => {
        if (d.unit === "mois") return xScale(parseFloat(d.durationValue)) - xScale(0);
        if (d.unit === "jours") return xScaleDays(parseFloat(d.durationValue)) - xScaleDays(0);
        return 0;
      })
      .attr("height", 10)
      // HIGHLIGHT LOGIC: Check originalRule.id
      .attr("fill", (d) => (d.originalRule && d.originalRule.id === selectedRuleId) ? "#FFD700" : "#05ed7d")
      .attr("opacity", 0.7)
      .style("cursor", onRuleClick ? "pointer" : "default")
      .on("click", (event, d) => {
          if (onRuleClickRef.current && d.originalRule) {
              onRuleClickRef.current(d.originalRule); 
              event.stopPropagation();
          }
      });

    svg.selectAll(".exclusivity-label")
      .data(flatPeriods)
      .enter()
      .append("text")
      .attr("class", "exclusivity-label")
      .attr("x", (d) => {
        let durationWidth = 0;
        if (d.unit === "mois") durationWidth = xScale(parseFloat(d.durationValue)) - xScale(0);
        if (d.unit === "jours") durationWidth = xScaleDays(parseFloat(d.durationValue)) - xScaleDays(0);
        return xScale(d.startTimeMonth) + durationWidth / 2;
      })
      .attr("y", height / 1.5 - 25)
      .attr("text-anchor", "middle")
      .style("font-size","0.6rem").style("fill", "#333")
      .text((d) => `${d.ruleType} ${d.durationValue} ${d.unit}`);
  }, [selectedRuleId, onRuleClick, selectedYear]);

  useEffect(() => {
        if (data && data.length > 0) {
            drawTimeline(data);
        } else {
            d3.select(chartRef.current).selectAll("*").remove();
        }
  }, [data, selectedRuleId, drawTimeline]);

  return (
    <div className="timeline-container module-section">
        <div className="module-section-title">Chronologie des médias</div>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }}></div>
    </div>
  );
};